import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { switchMap, tap, map } from 'rxjs/operators';
import { TaskResponse, TaskOrderRequest, TaskId } from '@business/entities/task';
import { TaskAPI } from '@business/api/task';
import { of } from 'rxjs';
import { UserId } from '@business/entities/account';
import { R } from '@core/utils/r';
import { OrgId } from '@business/entities/organization';

export class TodoBloc extends BaseBloc {
  private readonly todoRepo = new Repository<TaskResponse>({
    getItemId: todo => todo.id,
  });

  readonly tasks$ = this.todoRepo.items$;
  readonly selectTask = this.todoRepo.selectItem;

  onReset() {
    this.todoRepo.reset();
  }

  fetchTasks = (orgId?: OrgId) => {
    return TaskAPI.fetchTasks(orgId).pipe(this.todoRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_help_articles_error') }));
  };

  fetchTask = (id: TaskId) => {
    return TaskAPI.fetchTask(id).pipe(
      this.todoRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_help_articles_error') }),
    );
  };

  createTask = (task: Partial<TaskResponse>, parentId?: TaskId) => {
    return TaskAPI.createTask(task, parentId).pipe(
      tap(item => {
        if (parentId) {
          const ptask = this.todoRepo.getItem(parentId);
          const children = (ptask && ptask.children) || [];
          children.push(item);
          this.todoRepo.updateOne({
            id: parentId,
            changes: () => ({ children }),
          });
        } else {
          this.todoRepo.addOne(item);
        }
      }),
      handleMessage({
        type: requestMessage('create_task'),
      }),
    );
  };

  orderTasks = (orders: TaskOrderRequest) => {
    const updates = orders.map(item => ({ id: item.id, changes: () => ({ menuOrder: item.menuOrder }) }));
    this.todoRepo.updateMany(updates);
    return TaskAPI.setOrders(orders);
  };

  updateTask = (id: TaskId, task: Partial<TaskResponse>, isCalender = false) => {
    this.todoRepo.updateOne({ id, changes: () => task });
    return of(this.todoRepo.getItem(id)).pipe(
      switchMap(oldTask => {
        if (oldTask) {
          return TaskAPI.updateTask(id, { ...oldTask, ...task });
        }
        const allRepoItems = this.todoRepo.getAllItems() as TaskResponse[];
        const childTasks = R.flatten(allRepoItems.map(item => item.children || []));
        const oTask = childTasks.find(item => R.toNumber(item.id) == R.toNumber(id));
        if (oTask) {
          return TaskAPI.updateTask(id, { ...oTask, ...task });
        }
        return of(null);
      }),
      tap(item => {
        if (item) {
          this.todoRepo.upsertOne({ item });
        }
      }),
    );
  };

  setComplete = (id: TaskId, isCompleted: boolean) => {
    // this.todoRepo.updateOne({ id, changes: () => ({ isCompleted }) });
    return TaskAPI.setComplete(id, isCompleted).pipe(
      tap(item => {
        setTimeout(() => {
          this.todoRepo.upsertOne({ item });
        }, 2000);
      }),
    );
  };

  deleteTask = (id: TaskId) => {
    return TaskAPI.deleteTask(id).pipe(this.todoRepo.ops.removeOne(() => id));
  };

  search = (id: UserId) => {
    return TaskAPI.search(id).pipe(this.todoRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_help_articles_error') }));
  };
}
